<template>
  <OrderTestDialog
    :isShowDialog="isVisible"
    title="Processing Fee"
    width="580px"
    :showFooter="false"
    @close="close"
  >
    <div class="fee-dialog-content">
      <div class="standard-rule-section">
        <div class="rule-header">
          <span class="rule-icon">
            <img src="@/assets/orderTest/document-icon.svg" alt="Standard Rule" />
          </span>
          <h3 class="rule-title">Standard Rule</h3>
        </div>
        <ul class="rule-list">
          <li class="rule-item">
            <span>If your order total is <span class="highlight">below ${{ capFee }}</span>, a <span class="highlight">${{ processingFee }} processing fee</span> will be applied.</span>
          </li>
          <li class="rule-item">
            <span>Your final cost will be <span class="highlight">whichever is lower</span>:</span>
            <ul class="nested-list">
              <li>The <span class="highlight">${{ capFee }} payment cap</span>, or</li>
              <li>The <span class="highlight">order total +${{ processingFee }} processing fee</span></li>
            </ul>
          </li>
        </ul>
      </div>

      <div class="repeat-testing-section">
        <div class="rule-header">
          <span class="rule-icon repeat-icon">
            <img src="@/assets/orderTest/timer-icon.svg" alt="Repeat Testing" />
          </span>
          <h3 class="rule-title">Repeat Testing Discount</h3>
        </div>
        <ul class="rule-list">
          <li class="rule-item">
            <span>If a patient undergoes testing <span class="highlight">within {{ fromDays }} to {{ toDays }} days</span> of their previous test,</span>
          </li>
          <ul class="nested-list">
            <li>The payment cap to waive the ${{ processingFee }} processing fee is
              <span class="highlight">reduced to ${{ reducedCapFee }}</span></li>
          </ul>
        </ul>
      </div>

      <div class="exceptions-section">
        <div class="rule-header">
          <span class="rule-icon exception-icon">
            <img src="@/assets/orderTest/check-circle-icon.svg" alt="Exceptions" />
          </span>
          <h3 class="rule-title">Exceptions</h3>
        </div>
        <ul class="exception-list">
          <li class="rule-item">Food Sensitivity Profile 1 will not trigger processing fee</li>
        </ul>
      </div>
    </div>
  </OrderTestDialog>
</template>
<script>
import { mapGetters } from 'vuex';
import OrderTestDialog from '@/views/va-orderTest/components/OrderTestDialog.vue';

export default {
  components: {
    OrderTestDialog
  },
  props: {
    centerDialogVisible: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isVisible: false
    }
  },
  computed: {
    ...mapGetters('myPractice/billing', ['processingFeeSettings']),
    processingFee() {
      return this.processingFeeSettings?.lab_processing_fee;
    },
    capFee() {
      return this.processingFeeSettings?.cap_fee;
    },
    reducedCapFee() {
      return this.processingFeeSettings?.reduced_cap_fee;
    },
    fromDays() {
      return this.processingFeeSettings?.promotion_from_days;
    },
    toDays() {
      return this.processingFeeSettings?.promotion_to_days;
    }
  },
  watch: {
    centerDialogVisible(newVal) {
      if (newVal) {
        this.isVisible = newVal;
        this.fetchProcessingFeeSettings();
      }
    }
  },
  emits: ["close"],
  methods: {
    close() {
      this.isVisible = false;
      this.$emit("close");
    },
    async fetchProcessingFeeSettings() {
      try {
        const hasRequiredData =
          this.processingFeeSettings &&
          ['lab_processing_fee', 'cap_fee', 'reduced_cap_fee', 'promotion_from_days', 'promotion_to_days']
            .every(key => this.processingFeeSettings[key] !== undefined);
        if (!hasRequiredData) {
          await this.$store.dispatch('myPractice/billing/fetchProcessingFeeSettings');
        }
      } catch (error) {
        console.error('fetch data failure', error);
      }
    }
  }
}
</script>
<style scoped>
.fee-dialog-content {
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  color: #333;
  padding: 0;
}

.standard-rule-section,
.repeat-testing-section{
  border-radius: 4px;
  border-bottom: 1px dashed var(--DashedDivider, #C4C4C4);
  margin-bottom: 15px;
  padding-bottom: 15px;
}

.exceptions-section {
  border-radius: 4px;
  border-bottom: none;
  margin-bottom: 0;
}

.rule-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.rule-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.rule-icon img {
  width: 24px;
  height: 24px;
}

.rule-title {
  color: var(--VibrantDarkBlue, #004879);
  font-family: "IBM Plex Sans Hebrew";
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  margin: 0;
}

.rule-list,
.exception-list {
  list-style-type: none;
  padding-left: 40px;
  margin: 0;
}

.rule-item {
  position: relative;
  font-size: 14px;
  font-family: "IBM Plex Sans Hebrew";
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.25px;
}

.rule-item:before {
  content: "•";
  position: absolute;
  left: -20px;
  color: #004879;
}

.nested-list {
  padding-left: 20px;
  position: relative;
  font-size: 14px;
  font-family: "IBM Plex Sans Hebrew";
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.25px;
}

.nested-list li {
  position: relative;
  font-size: 14px;
  font-family: "IBM Plex Sans Hebrew";
}

.nested-list li:before {
  content: "•";
  position: absolute;
  left: -20px;
  color: #004879;
}

.highlight {
  color: var(--color-Black-000, #000);
  font-family: "IBM Plex Sans Hebrew";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.25px;
}
</style>
